import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Copy, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { FREEDOM_CAMPAIGN, isFreedomCampaignActive } from '../lib/campaign';

const SESSION_KEY = 'ss_freedom_popup_shown';
const SHOW_AFTER_MS = 1800;

/**
 * Independence Day FREEDOM SALE popup — active only during the campaign window
 * (09 Aug 2026 – 19 Aug 2026 IST). Shows once per browser session on the
 * homepage. Elegant Sachdeva Sons palette with subtle tricolour accents.
 */
export const FreedomSalePopup = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== '/') return undefined;
    if (!isFreedomCampaignActive()) return undefined;
    if (sessionStorage.getItem(SESSION_KEY) === '1') return undefined;
    const t = setTimeout(() => {
      sessionStorage.setItem(SESSION_KEY, '1');
      setOpen(true);
    }, SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [location.pathname]);

  if (!open) return null;

  const close = () => setOpen(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(FREEDOM_CAMPAIGN.code);
      localStorage.setItem('ss_applied_coupon', FREEDOM_CAMPAIGN.code);
      setCopied(true);
      toast.success(`Code ${FREEDOM_CAMPAIGN.code} copied`);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Copy failed — long-press to select the code');
    }
  };

  const shopTheSale = () => {
    localStorage.setItem('ss_applied_coupon', FREEDOM_CAMPAIGN.code);
    close();
    navigate('/search?sort=newest');
  };

  return (
    <div
      className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300"
      data-testid="freedom-popup"
      role="dialog"
      aria-modal="true"
      aria-label="Freedom Sale — 20% off with code FREEDOM20"
      onClick={close}
    >
      <div
        className="relative max-w-md w-full bg-[#FAF9F7] rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Tricolour top ribbon */}
        <div className="flex h-1.5 w-full" aria-hidden="true">
          <div className="flex-1 bg-[#FF9933]" />
          <div className="flex-1 bg-white" />
          <div className="flex-1 bg-[#138808]" />
        </div>

        <button
          type="button"
          onClick={close}
          data-testid="freedom-popup-close"
          aria-label="Close"
          className="absolute top-4 right-4 z-10 text-white/85 hover:text-white transition"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header band */}
        <div className="relative bg-gradient-to-br from-[#6B4E37] via-[#8B6F4E] to-[#2C2C2C] text-[#FAF9F7] px-6 pt-8 pb-10 text-center overflow-hidden">
          {/* Ashoka-blue chakra glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-25"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 20%, rgba(255,153,51,.55) 0px, transparent 90px), radial-gradient(circle at 80% 80%, rgba(19,136,8,.5) 0px, transparent 90px), radial-gradient(circle at 50% 50%, rgba(0,0,128,.35) 0px, transparent 120px)',
            }}
          />
          <p className="relative font-montserrat text-[11px] tracking-[0.4em] uppercase opacity-95 mb-2">
            <span aria-hidden="true">🇮🇳</span> Freedom Sale
          </p>
          <h2 className="relative font-seasons text-4xl sm:text-5xl leading-[1.05]">
            20% OFF
          </h2>
          <p className="relative font-montserrat text-sm mt-3 text-[#FAF6EE]">
            Celebrate Independence with Style
          </p>
          <p className="relative font-montserrat text-[11px] mt-2 tracking-[0.25em] uppercase text-[#E8D8C5]">
            09 – 19 August 2026
          </p>
        </div>

        {/* Code block */}
        <div className="px-6 -mt-5 relative">
          <div className="bg-white border-2 border-dashed border-[#C4B5A0] rounded-lg py-4 px-3 text-center shadow-md">
            <p className="font-montserrat text-[10px] tracking-[0.3em] uppercase text-[#8B7D6B]">Use code</p>
            <div className="font-seasons text-4xl text-[#6B4E37] tracking-wider mt-1" data-testid="freedom-popup-code">
              {FREEDOM_CAMPAIGN.code}
            </div>
            <p className="font-montserrat text-xs text-[#8B7D6B] mt-1">
              {FREEDOM_CAMPAIGN.discountPercent}% OFF the entire catalogue
            </p>
          </div>
        </div>

        {/* CTAs */}
        <div className="px-6 py-5 space-y-2">
          <Button
            onClick={shopTheSale}
            data-testid="freedom-popup-shop"
            className="w-full bg-[#6B4E37] hover:bg-[#5A4030] text-white font-montserrat tracking-[0.2em] uppercase text-xs py-6"
          >
            Shop the Sale <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            onClick={copyCode}
            variant="outline"
            data-testid="freedom-popup-copy"
            className="w-full border-[#C4B5A0] text-[#6B4E37] hover:bg-[#FAF6EE] font-montserrat tracking-[0.2em] uppercase text-xs py-6"
          >
            {copied ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? 'Code copied' : `Copy ${FREEDOM_CAMPAIGN.code}`}
          </Button>
          <button
            type="button"
            onClick={close}
            data-testid="freedom-popup-skip"
            className="block w-full text-[11px] font-montserrat text-[#8B7D6B] hover:text-[#2C2C2C] mt-2 underline-offset-2 hover:underline"
          >
            No thanks, I&apos;ll skip the discount
          </button>
        </div>

        {/* Tricolour bottom ribbon */}
        <div className="flex h-1.5 w-full" aria-hidden="true">
          <div className="flex-1 bg-[#FF9933]" />
          <div className="flex-1 bg-white" />
          <div className="flex-1 bg-[#138808]" />
        </div>
      </div>
    </div>
  );
};
